/**
 * Fetches `clinical_document.document_page_mapping` from the Firestore medical
 * PA order document. Mapping is not yet reliably available on the Postgres
 * general-order payload, so the PDF viewers (Medical Necessity, fax
 * questionnaire) still read it from Firestore.
 * @module useDocumentPageMapping
 */
import { useEffect, useState } from "react";
import { FirestoreService } from "../api/firebase/firestoreService";
import { FirestoreDocumentReference } from "../api/firebase/references";
import {
  DocumentPageMapping,
  MedicalPaOrder,
} from "../data-model/medicalPaOrdersModel";
import { logError } from "../utils/customLogger";

/** Resolved Firestore document-page mapping for one order. */
export interface UseDocumentPageMappingResult {
  /** Mapping entries from Firestore, or undefined while loading / on failure. */
  documentPageMapping: DocumentPageMapping[] | undefined;
  /** True while the Firestore order document is being fetched. */
  isLoading: boolean;
}

/**
 * Loads `document_page_mapping` from Firestore `medical_pa_orders/{orderId}`.
 *
 * @param orderId - Medical PA order ID; fetch is skipped when undefined
 * @returns Mapping array and loading flag
 */
export const useDocumentPageMapping = (
  orderId: string | undefined,
): UseDocumentPageMappingResult => {
  const [documentPageMapping, setDocumentPageMapping] = useState<
    DocumentPageMapping[] | undefined
  >(undefined);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!orderId) {
      setDocumentPageMapping(undefined);
      setIsLoading(false);
      return undefined;
    }

    let isActive = true;
    setIsLoading(true);
    setDocumentPageMapping(undefined);

    FirestoreService.getDocument<MedicalPaOrder>(
      FirestoreDocumentReference.medicalPaOrders(orderId),
    )
      .then((order) => {
        if (!isActive) return;
        setDocumentPageMapping(order.clinical_document?.document_page_mapping);
      })
      .catch((error) => {
        logError(
          error as Error,
          "Error loading document_page_mapping from Firestore",
        );
        if (isActive) setDocumentPageMapping(undefined);
      })
      .finally(() => {
        if (isActive) setIsLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [orderId]);

  return { documentPageMapping, isLoading };
};
